import React, { useCallback, useMemo, useState } from "react"
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Box,
  Button,
  Flex,
  Text,
  Image,
  Center,
  useToken,
  SystemStyleObject,
} from "@chakra-ui/react"

import { useCartContext } from "@app/providers/cart"
import { useConfigContext } from "@app/providers/config"
import { useLiveProducts } from "@app/hooks/useLiveProducts"
import { useCart } from "@app/hooks/useCart"
import { useColorPreset } from "@app/hooks/useColorPreset"
import Icon from "@app/components/Icon"
import ResolvedContent from "@app/components/ResolvedContent"
import { NormalisedProduct } from "@root/types/product"
import { useShopifyPrice } from "@app/hooks/useShopify"
import { useMedia } from "@app/hooks/useMedia"

type Props = {
  listRef?: React.Ref<HTMLDivElement>
}

type GiftWrapProductItemProps = {
  product: NormalisedProduct & { productTitle?: string }
  onAdd: (product: NormalisedProduct) => void
  isLoading: boolean
  isDisabled: boolean
  numItems: number
}

const GiftWrapProductItem: React.FC<GiftWrapProductItemProps> = ({ product, onAdd, isLoading, isDisabled, numItems }) => {
  const { formattedPrice } = useShopifyPrice(product?.variants?.[0], 1)
  const { isXSmall } = useMedia()
  const [white, purple] = useToken("colors", ["primary.white", "preset.purple"])

  const is3Items = numItems >= 3

  const hoverStyle: SystemStyleObject = { boxShadow: `0 0 0 2px ${white}, 0 0 0 4px ${purple}` }

  return (
    <Flex
      key={product.id}
      background="primary.white"
      flexDirection={{ base: "row", xs: is3Items ? "column" : "row" }}
      alignItems={{ base: "center", xs: "center" }}
      borderRadius="8px"
      flex="1"
      overflow="hidden"
      _hover={{ base: {}, xs: hoverStyle }}
    >
      <Box w={{ base: "50%", xs: is3Items ? "80%" : "50%" }}>
        {product?.images[0] && <Image src={product.images[0].src} pointerEvents="none" w="100%" />}
      </Box>
      {!isXSmall ? (
        <Flex flexDirection="column" p={3} w="50%">
          <Flex flexDirection="column" flex={1} mt={0}>
            <Text size="sm" variant="medium" mb={0}>
              {product.productTitle}
            </Text>
            <Text size="sm">{formattedPrice}</Text>
          </Flex>
          <Button
            onClick={() => onAdd(product)}
            variant="secondary"
            isLoading={isLoading}
            isDisabled={isDisabled}
            mt="auto"
            w="fit-content"
            alignSelf="flex-end"
          >
            <Text size="sm" variant="normal">
              Add
            </Text>
          </Button>
        </Flex>
      ) : (
        <Flex flexDirection="column" p={3} w={is3Items ? "100%" : "50%"}>
          <Flex flexDirection="column" flex="unset" mt={2}>
            <Text size="sm" variant="medium" mb={1}>
              {product.productTitle}
            </Text>
            {!is3Items && <Text size="sm">{formattedPrice}</Text>}
          </Flex>
          <Flex justify="space-between">
            {is3Items && <Text size="sm">{formattedPrice}</Text>}
            <Button
              onClick={() => onAdd(product)}
              variant="secondary"
              isLoading={isLoading}
              isDisabled={isDisabled}
              mt="auto"
              w="fit-content"
              alignSelf="flex-end"
              ml="auto"
            >
              <Text size="sm" variant="normal">
                Add
              </Text>
            </Button>
          </Flex>
        </Flex>
      )}
    </Flex>
  )
}

export const CartGiftWrap: React.FC<Props> = React.memo(({ listRef }) => {
  const { count, giftWrapActive } = useCartContext()
  const {
    settings: { cart },
  } = useConfigContext()
  const { addToCart, loading: cartLoading, giftWrapping } = useCart()
  const [addingId, setAddingId] = useState<string | null>(null)

  const rawProducts = useMemo(
    () =>
      giftWrapping?.product?.map(product => ({
        ...product?._rawProduct,
        productTitle: product?.productTitle,
      })) || [],
    [giftWrapping?.product]
  )

  const { liveProducts } = useLiveProducts(rawProducts)

  const giftWrapProducts = liveProducts.map((liveProduct: NormalisedProduct) => {
    const staticProduct = rawProducts.find(({ shopify }) => shopify.handle === liveProduct.handle)
    return {
      ...liveProduct,
      productTitle: staticProduct?.productTitle,
    }
  })

  const { color } = useColorPreset(giftWrapping?.color)
  const { color: bodyColor } = useColorPreset(giftWrapping?.bodyColor)
  const resolvedTextColor = `primary.${giftWrapping?.textColor === "light" ? "white" : "black"}`

  const handleAddToCart = useCallback(
    async (product: NormalisedProduct) => {
      if (!product || cartLoading || addingId) return
      setAddingId(product.id)
      try {
        await addToCart(product, product?.variants[0], 1, [
          {
            key: cart.giftWrapAttribute,
            value: "true",
          },
        ])
        if (listRef && typeof listRef === "object" && "current" in listRef && listRef.current) {
          listRef.current.scrollTo({ top: 0, behavior: "smooth" })
        }
      } finally {
        setAddingId(null)
      }
    },
    [listRef, addToCart, cartLoading, addingId, cart.giftWrapAttribute]
  )

  const handleAccordionToggle = useCallback(
    (isExpanded: boolean) => {
      if (!isExpanded && listRef && typeof listRef === "object" && "current" in listRef && listRef.current) {
        setTimeout(() => {
          if (listRef && typeof listRef === "object" && "current" in listRef && listRef.current) {
            listRef.current.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" })
          }
        }, 300)
      }
    },
    [listRef]
  )

  return giftWrapping && count > 0 && !giftWrapActive ? (
    <Box mx={0} mb={3}>
      <Accordion allowToggle>
        <AccordionItem border="unset" borderRadius="10px" overflow="hidden">
          {({ isExpanded }) => (
            <>
              <AccordionButton
                bg={color}
                color={resolvedTextColor}
                _hover={{ bgColor: color }}
                py={4}
                px={4}
                onClick={() => handleAccordionToggle(isExpanded)}
              >
                <Flex alignItems="center" justifyContent="space-between" w="full">
                  <Flex alignItems="center" gap={2}>
                    <Center w="40px" h="40px" minW="40px" borderRadius="full" bg="primary.white" overflow="hidden">
                      <Icon name="gift" width="20px" height="20px" stroke="preset.blue" />
                    </Center>
                    <Text size="md">{giftWrapping?.title}</Text>
                  </Flex>
                  <Center w="40px" h="40px" borderRadius="20px" bg="primary.white" border="1px solid" borderColor="secondary.grey">
                    <Icon name={isExpanded ? "minus" : "plus"} width="12px" height="12px" color="grey.dark" />
                  </Center>
                </Flex>
              </AccordionButton>
              <AccordionPanel pb={4} px={4} bg={bodyColor}>
                <Flex flexDirection={{ base: "column", xs: "row" }} gap={3} flexWrap="wrap">
                  {giftWrapProducts.map((product, index) => (
                    <GiftWrapProductItem
                      key={`${product.id}-${index}`}
                      product={product}
                      onAdd={handleAddToCart}
                      isLoading={addingId === product.id}
                      isDisabled={addingId !== null && addingId !== product.id}
                      numItems={giftWrapProducts.length}
                    />
                  ))}
                </Flex>
                {giftWrapping.content && (
                  <Box mt={4}>
                    <ResolvedContent
                      content={giftWrapping.content}
                      sx={{
                        ".content-p": {
                          fontSize: "xs",
                          m: 0,
                          mb: 2,
                        },
                      }}
                    />
                  </Box>
                )}
              </AccordionPanel>
            </>
          )}
        </AccordionItem>
      </Accordion>
    </Box>
  ) : null
})
